// ============================================
// InventoryTableRow Component
// Single row renderer for inventory table
// ============================================

import { useI18n } from '../../../i18n';
import { StockProgressBar } from '../../../components/shared';
import { MoreVerticalIcon, LocationIcon } from '../../../components/icons';
import { cn } from '../../../utils/cn';
import { useMoney } from '../../../providers/CurrencyProvider';
import { resolveStockStatus, type StockStatusValue } from '../../../utils/stockStatus';

// ============================================
// Types
// ============================================

export interface InventoryItem {
  id: string;
  productId: string;
  productName: string;
  sku: string;
  imageUrl?: string;
  address: string;
  shelfLocation: string;
  currentQuantity: number;
  preOrderedQuantity: number;
  availableQuantity: number;
  minQuantity: number;
  maxQuantity: number;
  salePrice: number | null;
  msrpPrice: number | null;
  cost?: number | null;
  margin?: number | null;
  /** Tính bằng utils/stockStatus.ts — cùng hàm với bộ lọc Status của trang. */
  status: StockStatusValue;
  locationPercent: number;
  locationTrend: 'up' | 'down' | 'stable';
  profit: number;
  lastCountedAt: string | null;
  updatedAt: string;
}

interface InventoryTableRowProps {
  item: InventoryItem;
  onActionClick?: (item: InventoryItem) => void;
}

// ============================================
// Helpers
// ============================================

// Bug #160: 2 cột này từng hardcode "PLN" trong khi COST/PROFIT ngay cạnh lại
// hiện "$" — cùng một bảng, hai đơn vị tiền. Giờ mọi cột đi chung `useMoney()`,
// tức cùng một tiền tệ do trang đặt (sheet 2 #75). Đừng import formatMoney
// trực tiếp ở đây nữa: làm vậy là quay lại đúng bug cũ, chỉ khác nguyên nhân.

function formatDate(dateStr: string): string {
  if (!dateStr) return '—';
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '—';
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  } catch {
    return '—';
  }
}

// ============================================
// Cell Components
// ============================================

function ImageCell({ imageUrl, productName }: { imageUrl?: string; productName: string }) {
  return (
    <div className="flex h-12 w-12 items-center justify-center overflow-hidden rounded-lg bg-orange-100">
      {imageUrl ? (
        <img src={imageUrl} alt={productName} className="h-full w-full object-cover" />
      ) : (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <rect x="3" y="3" width="18" height="18" rx="2" fill="#FB923C" opacity="0.3" />
          <path d="M3 9l4-4 4 4 4-4 4 4" stroke="#FB923C" strokeWidth="1.5" strokeLinecap="round" />
          <circle cx="8" cy="14" r="2" fill="#FB923C" opacity="0.6" />
        </svg>
      )}
    </div>
  );
}

function ProductCell({ name, sku }: { name: string; sku: string }) {
  return (
    <div>
      <p className="text-sm font-semibold text-[#212121]">{name}</p>
      <p className="text-xs text-slate-400">{sku}</p>
    </div>
  );
}

function AddressCell({ address, shelf }: { address: string; shelf: string }) {
  return (
    <div className="flex items-start gap-1">
      <LocationIcon size={14} className="mt-0.5 shrink-0 text-slate-400" />
      <div>
        <p className="text-sm font-semibold text-[#212121]">{address}</p>
        <p className="text-[10px] text-slate-400">{shelf}</p>
      </div>
    </div>
  );
}

function QuantityCell({
  current,
  preOrdered,
  available,
  max,
  status,
  minQuantity = 0,
}: {
  current: number;
  preOrdered: number;
  available: number;
  max: number;
  /** Trạng thái đã tính sẵn của dòng; thiếu thì suy diễn bằng CÙNG hàm dùng chung. */
  status?: StockStatusValue;
  minQuantity?: number;
}) {
  const { t } = useI18n();
  const safeMax = Math.max(max, 1);
  // Bug #181: trước đây ô này tô đỏ theo tỷ lệ `available / max < 0.2` — một
  // định nghĩa "sắp hết" THỨ BA, khác cả badge lẫn bộ lọc Status. Kết quả là một
  // dòng có thể đang tô đỏ "sắp hết" nhưng lại không hiện ra khi lọc "Low Stock".
  // Giờ dùng chung trạng thái với hai chỗ kia.
  const effectiveStatus = status ?? resolveStockStatus(null, current, minQuantity);
  const isLowStock = effectiveStatus !== 'IN_STOCK';

  return (
    <div className="space-y-1">
      <p className="text-sm font-semibold leading-[22px]">
  <span className="text-[#212121]">{current}</span>
  <span className="mx-0.5 text-[#CBD5E1]">/</span>
  <span className="text-[#0288D1]">{preOrdered}</span>
</p>
      <StockProgressBar available={available} total={safeMax} />
      <p className={cn('text-[10px] font-bold', isLowStock ? 'text-red-500' : 'text-emerald-600')}>
        {available} {t('categoriesActive')}
      </p>
    </div>
  );
}

function BasePriceCell({ basePrice }: { basePrice: number | null }) {
  const money = useMoney();
  if (basePrice === null) return <p className="text-xs text-slate-400">—</p>;
  return <p className="text-sm font-semibold text-slate-700">{money.format(basePrice)}</p>;
}

function LocationPriceCell({ salePrice }: { salePrice: number | null }) {
  const money = useMoney();
  if (salePrice === null) return <p className="text-xs text-slate-400">—</p>;
  return (
    <div>
      <p className="text-sm font-semibold text-[#0288D1]">{money.format(salePrice)}</p>
      <p className="text-[10px] font-semibold text-[#0288D1]">(0%)</p>
    </div>
  );
}

function CostCell({ cost }: { cost?: number | null }) {
  const money = useMoney();
  if (cost == null) return <p className="text-xs text-slate-400">—</p>;
  return <p className="text-sm font-semibold text-slate-700">{money.format(cost)}</p>;
}

function MarginCell({ margin }: { margin?: number | null }) {
  if (margin == null) return <p className="text-xs text-slate-400">—</p>;
  return <p className="text-sm font-semibold text-slate-700">{margin.toFixed(2)}%</p>;
}

function ProfitUnitCell({ profit }: { profit: number }) {
  const money = useMoney();
  const isPositive = profit >= 0;
  return (
    <p className={cn('text-sm font-bold', isPositive ? 'text-emerald-600' : 'text-red-500')}>
      {money.signed(profit)}
    </p>
  );
}

function DateCell({ dateStr }: { dateStr: string }) {
  return <p className="text-xs font-medium text-slate-600">{formatDate(dateStr)}</p>;
}

// ============================================
// Component
// ============================================

export function InventoryTableRow({ item, onActionClick }: InventoryTableRowProps) {
  return (
    <>
      <td className="px-4 py-3">
        <ImageCell imageUrl={item.imageUrl} productName={item.productName} />
      </td>
      <td className="px-4 py-3">
        <ProductCell name={item.productName} sku={item.sku} />
      </td>
      <td className="px-4 py-3">
        <AddressCell address={item.address} shelf={item.shelfLocation} />
      </td>
      <td className="px-4 py-3">
        <QuantityCell
          current={item.currentQuantity}
          preOrdered={item.preOrderedQuantity}
          available={item.availableQuantity}
          max={item.maxQuantity}
          status={item.status}
          minQuantity={item.minQuantity}
        />
      </td>
      <td className="px-4 py-3">
        <BasePriceCell basePrice={item.msrpPrice} />
      </td>
      <td className="px-4 py-3">
        <LocationPriceCell salePrice={item.salePrice} />
      </td>
      <td className="px-4 py-3">
        <CostCell cost={item.cost} />
      </td>
      <td className="px-4 py-3">
        <MarginCell margin={item.margin} />
      </td>
      <td className="px-4 py-3">
        <ProfitUnitCell profit={item.profit} />
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center justify-between">
          <DateCell dateStr={item.updatedAt} />
          <button
            onClick={(e) => {
              e.stopPropagation();
              onActionClick?.(item);
            }}
            className="ml-2 rounded-lg p-1 text-slate-400 opacity-0 transition-opacity group-hover:opacity-100 hover:bg-slate-100 hover:text-slate-600"
          >
            <MoreVerticalIcon size={16} />
          </button>
        </div>
      </td>
    </>
  );
}

// Export cell components for reuse
export const InventoryCells = {
  ImageCell,
  ProductCell,
  AddressCell,
  QuantityCell,
  BasePriceCell,
  LocationPriceCell,
  CostCell,
  MarginCell,
  ProfitUnitCell,
  ProfitCell: ProfitUnitCell,
  DateCell,
  // Legacy aliases
  PriceCell: ({ salePrice, msrp }: { salePrice: number | null; msrp: number | null }) => (
    <BasePriceCell basePrice={msrp ?? salePrice} />
  ),
  // Badge trạng thái tồn kho.
  //
  // Bug #181: bản cũ tự chuẩn hoá tại chỗ và mặc định về OUT_OF_STOCK khi không
  // đọc được `status` — dòng còn đầy hàng nhưng payload thiếu field vẫn bị tô
  // "hết hàng", trong khi bộ lọc lại xếp nó là IN_STOCK. Giờ cả hai đi qua
  // utils/stockStatus.ts: badge và bộ lọc không thể lệch nhau nữa.
  StockStatusCell: ({
    status,
    minQuantity,
    currentQuantity = 0,
  }: {
    status?: string | null;
    minQuantity: number;
    currentQuantity?: number;
  }) => {
    const styles: Record<StockStatusValue, string> = {
      IN_STOCK: 'bg-emerald-50 text-emerald-700',
      LOW_STOCK: 'bg-amber-50 text-amber-700',
      OUT_OF_STOCK: 'bg-red-50 text-red-700',
    };
    const safeStatus = resolveStockStatus(status, currentQuantity, minQuantity);
    return (
      <div className="space-y-1">
        <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-bold ${styles[safeStatus]}`}>
          {safeStatus.replace(/_/g, ' ')}
        </span>
        <p className="text-[10px] text-slate-500">Min: {minQuantity}</p>
      </div>
    );
  },
  LocationPercentCell: ({ percent, trend }: { percent: number; trend: 'up' | 'down' | 'stable' }) => {
    const color = trend === 'up' ? 'text-emerald-600' : trend === 'down' ? 'text-red-600' : 'text-slate-500';
    return <span className={`text-xs font-bold ${color}`}>{percent}%</span>;
  },
};
